/*
  <free-shipping-bar> — keeps the cart progress bar in sync with the cart.

  - Listens for the theme's `cart:updated` event (fired by Impulse's CartForm for the
    drawer and the cart page) and re-renders from event.detail.cart.
  - Thresholds arrive in store-currency cents (USD). When the shopper is viewing in
    another currency (the Canada market shows CAD) the threshold is converted with
    Shopify.currency.rate so it is compared against cart.total_price in like units.
  - Money is formatted with the theme's own formatter so it matches every other price.
*/
(function () {
  if (window.customElements && customElements.get('free-shipping-bar')) return;

  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      this.textEl = this.querySelector('[data-fsb-text]');
      this.fillEl = this.querySelector('[data-fsb-fill]');
      this.trackEl = this.querySelector('[data-fsb-track]');
      if (!this.textEl || !this.fillEl || !this.trackEl) return;

      var storeCents = parseInt(this.dataset.thresholdCents, 10) || 0;
      var rate = 1;
      if (window.Shopify && Shopify.currency && Shopify.currency.rate) {
        var r = parseFloat(Shopify.currency.rate);
        if (!isNaN(r) && r > 0) rate = r;
      }
      // Threshold in the currency the cart is actually priced in.
      this.threshold = Math.round(storeCents * rate);

      this._onCartUpdated = this._onCartUpdated.bind(this);
      this._onProductAdded = this._onProductAdded.bind(this);
      // Impulse fires cart:updated only from its quantity-change handler. Its add-to-cart
      // path rebuilds the drawer without any cart event, so also listen for the product
      // form's ajaxProduct:added, which bubbles to document after the add has completed.
      document.addEventListener('cart:updated', this._onCartUpdated);
      document.addEventListener('ajaxProduct:added', this._onProductAdded);

      // Server-side Liquid cannot convert currencies, so a converted cart arrives as
      // "pending" and needs one fetch to settle. Same-currency carts rendered exactly.
      if (this.dataset.state === 'pending') this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this._onCartUpdated);
      document.removeEventListener('ajaxProduct:added', this._onProductAdded);
    }

    _onProductAdded() {
      this.refresh();
    }

    _onCartUpdated(evt) {
      var cart = evt && evt.detail && evt.detail.cart;
      if (cart && typeof cart.total_price === 'number') {
        this.render(cart.total_price, cart.item_count);
      } else {
        this.refresh();
      }
    }

    refresh() {
      var self = this;
      fetch((window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) { self.render(cart.total_price, cart.item_count); })
        .catch(function () { /* leave the last known state in place */ });
    }

    formatMoney(cents) {
      var out;
      if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
        out = theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
      } else {
        out = '$' + (cents / 100).toFixed(2);
      }
      // The theme's formatter can wrap the value in <span class="money">; the bar's text
      // is set as plain text, so strip any markup to match the Liquid-rendered output.
      return String(out).replace(/<[^>]*>/g, '');
    }

    render(total, itemCount) {
      var state, pct, msg;
      if (!itemCount) {
        state = 'empty'; pct = 0; msg = this.dataset.textEmpty || '';
      } else if (total >= this.threshold) {
        state = 'reached'; pct = 100; msg = this.dataset.textReached || '';
      } else {
        state = 'progress';
        pct = Math.max(2, Math.round((total / this.threshold) * 100));
        msg = this.dataset.textProgress || '';
      }
      // The goal reads better without ".00" ("$100" not "$100.00"); the remaining amount keeps its cents.
      msg = msg
        .replace('[amount]', this.formatMoney(Math.max(0, this.threshold - total)))
        .replace('[threshold]', this.formatMoney(this.threshold).replace(/\.00(?!\d)/, ''));

      this.dataset.state = state;
      this.fillEl.style.width = pct + '%';
      this.trackEl.setAttribute('aria-valuenow', pct);
      if (this.textEl.textContent !== msg) this.textEl.textContent = msg;
    }
  }

  customElements.define('free-shipping-bar', FreeShippingBar);
})();
